import { Col, Form, Row } from "antd";
import { Formik } from "formik";
import moment from "moment";
import React, { SyntheticEvent, useEffect, useState } from "react";
import { useLocation } from "react-router";
import { FundStatusBE } from "../../../../../enums/fundStatuses";
import { NotificationTypes } from "../../../../../enums/notificationTypes";
import { Fund } from "../../../../../models/Fund/fund.model";
import { ProfileModel } from "../../../../../models/HFund/hfund.model";
import { FundsService } from "../../../../../services/FundService/Fund.service";
import { MetaService } from "../../../../../services/MetaService/meta.service";
import DatePickerComponent from "../../../../../shared/components/DatePickerComponent";
import DropdownField from "../../../../../shared/components/DropdownField";
import InputField from "../../../../../shared/components/InputField";
import Notification from "../../../../../shared/components/Notification";
import { UIModal } from "../../../../../shared/components/UIModal";
import { LocalStorage } from "../../../../../shared/utils/localStorageHelpers";
import "./fund-form.scss";
import {
  fundStatuses,
  fundStrategies,
  fundStructure,
  feederFund,
  productsTraded,
  geographicFocus,
  fundBaseCurrencies,
  fundSubStrategies,
} from "./fundDefinitions";
import { FundFormValidation } from "./fundFormValidation";

interface FundFormProps {
  visible: boolean;
  title: string;
  closeHandler: (fund?: Fund) => void;
  data?: Fund;
  disableTerminatedStatus?: boolean;
}

const FundForm = (props: FundFormProps) => {
  const { visible, title, closeHandler, data, disableTerminatedStatus } = props;
  const { state = {} }: { state: any } = useLocation();

  const [fundFormData, setFundFormData] = useState<Fund>(new Fund());
  const [countries, setCountries] = useState<any>();
  const [provinces, setProvinces] = useState<any>();
  const [cities, setCities] = useState<any>();
  const [companyid, setCompanyId] = useState<string>();
  const [availableStatus, setAvailableStatus] = useState<any[]>();
  const [buttonLoader, setButtonLoader] = useState<boolean>(false);

  const fetchCountries = () => {
    MetaService.listCountries(
      (countries) => {
        setCountries(countries);
      },
      () => {
      }
    ).then();
  };

  const fetchProvinces = (countryId: any) => {
    if (!countryId) {
      return;
    }
    MetaService.listProvinces(
      countryId,
      (provinces) => {
        setProvinces(provinces);

        if (fundFormData?.id) {
          fetchCities(
            fundFormData?.addresses[0]?.countryId,
            fundFormData?.addresses[0]?.provinceId
          );
        }
      },
      () => {
      }
    ).then();
  };
  const fetchCities = (countryId: any, provinceId: any) => {
    if (!countryId || !provinceId) {
      return;
    }
    MetaService.listCities(
      countryId,
      provinceId,
      (cities) => {
        setCities(cities);
      },
      () => { }
    );
  };
  const fetchFundDetails = (id: number) => {
    let companyId = state?.companyId;
    if (!companyId) {
      const user = LocalStorage.getItem("USER") as ProfileModel;
      // setCompanyId(companyId);
      companyId = user?.companyId;
    }
    FundsService.fetchSingleFund(
      id.toString(),
      { companyId },
      (fund: Fund) => {
        setFundFormData(fund);
        fetchProvinces(fund?.addresses[0]?.countryId);
        fetchCities(
          fund?.addresses[0]?.countryId,
          fund?.addresses[0]?.provinceId
        );
      },
      (err) => {
        // ;
        // Notification({
        //   message: "Hedge Fund",
        //   description: "Unable to fetch fund details",
        //   type: NotificationTypes.ERROR,
        // });
      },
      () => { }
    );
  };
  const onSubmit = (fund: Fund, resetForm: Function) => {
    setButtonLoader(true);
    let companyId = companyid || state?.companyId;
    if (!companyId) {
      const user = LocalStorage.getItem("USER") as ProfileModel;
      companyId = user.companyId;
    }
    if (fund.id) {
      FundsService.updateFund(
        fund,
        { companyId },
        (res) => {
          resetForm();
          closeHandler(res);
          Notification({
            message: "Hedge Fund",
            description: "Fund updated successfully",
            type: NotificationTypes.SUCCESS,
          });
        },
        (err) => {
          // Notification({
          //   message: "Hedge Fund",
          //   description: "Fund updation failed",
          //   type: NotificationTypes.ERROR,
          // });
        },
        () => {
          setButtonLoader(false)
        }
      );
    } else {
      FundsService.createFund(
        fund,
        { companyId },
        (res: Fund) => {
          resetForm();
          closeHandler(res);
          Notification({
            message: "Hedge Fund",
            description: "Fund created successfully",
            type: NotificationTypes.SUCCESS,
          });
        },
        (err) => {
          // Notification({
          //   message: "Hedge Fund",
          //   description: "Fund creation failed",
          //   type: NotificationTypes.ERROR,
          // });
        },
        () => {
          setButtonLoader(false)
        }
      );
    }
  };

  useEffect(() => {
    const user = LocalStorage.getItem("USER") as any;
    setCompanyId(user?.companyId ?? user?.company_id);
    if (data?.id) {
      fetchFundDetails(data.id);
    }
  }, [data]);

  useEffect(() => {
    if (disableTerminatedStatus) {
      setAvailableStatus(
        fundStatuses.filter((sts) => sts.value !== FundStatusBE.TERMINATED)
      );
    } else {
      setAvailableStatus(fundStatuses);
    }

    fetchCountries();
  }, []);

  return (
    <Formik enableReinitialize
      initialValues={fundFormData}
      onSubmit={() => { }}
      validationSchema={FundFormValidation}
    >
      {({ setFieldValue, values, dirty, resetForm, isValid, setFieldTouched }) => {
        return (
          <UIModal
            className="fund-form__modal"
            visible={visible}
            closeModal={closeHandler}
            title={(fundFormData.id ? "Edit " : "Add New ") + " Fund"}
            submitProps={{
              text: fundFormData.id ? "Update " : "Create",
              clickHandler: () => {
                onSubmit(values, resetForm);
              },
              disabled: !isValid || !dirty,
              loading: buttonLoader
            }}
            cancelProps={{
              text: "Cancel",
              clickHandler: () => {
                resetForm();
                closeHandler();
              },
            }}
          >
            <Form>
              <div className="fund-form__container">
                <Row>
                  <Col sm={12} className="fund-form__field">
                    <InputField
                      placeholder={"Fund Name *"}
                      name="name"
                      type="text"
                      value={values?.name}
                    />
                  </Col>
                </Row>
                <Row>
                  <Col sm={12} className="fund-form__field">
                    <DropdownField
                      mode="single"
                      optionFilterProp="label"
                      placeholder={"Fund Domicile *"}
                      name="countryId"
                      options={countries}
                      showSearch={false}
                      value={values.countryId}
                      setFieldTouched={() => setFieldTouched('countryId')}
                      onChange={(value) => {
                        setFieldValue("countryId", value);
                        fetchProvinces(value);
                      }}
                    />
                  </Col>
                  <Col sm={12} className="fund-form__field">
                    <DropdownField
                      mode="single"
                      optionFilterProp="label"
                      placeholder={"Fund Structure"}
                      name="fundStructure"
                      options={fundStructure}
                      showSearch={false}
                      value={values.fundStructure}
                      setFieldTouched={() => setFieldTouched('fundStructure')}
                      onChange={(value) =>
                        setFieldValue("fundStructure", value)
                      }
                    />
                  </Col>
                </Row>
                <Row>
                  <Col sm={12} className="fund-form__field">
                    <DropdownField
                      mode="single"
                      optionFilterProp="label"
                      placeholder={"Feeder Fund"}
                      name="feederFund"
                      options={feederFund}
                      showSearch={false}
                      value={
                        values?.id
                          ? !!values?.feederFundName
                          : values?.feederFund
                      }
                      setFieldTouched={() => setFieldTouched('feederFund')}
                      onChange={(value) => {
                        setFieldValue("feederFund", value);
                        if (!value) {
                          setFieldValue("feederFundName", null);
                        }
                      }}
                    />
                  </Col>
                  {(values?.feederFund ||
                    (values?.id && values?.feederFundName)) && (
                      <Col sm={12} className="fund-form__field">
                        <InputField
                          placeholder={"Feeder Fund Name (if applicable)"}
                          name="feederFundName"
                          type="text"
                          value={values?.feederFundName}
                        />
                      </Col>
                    )}
                </Row>
                <Row>
                  <Col sm={12} className="fund-form__field">
                    <DropdownField
                      mode="single"
                      optionFilterProp="label"
                      placeholder={"Status *"}
                      name="status"
                      setFieldTouched={() => setFieldTouched('status')}
                      options={availableStatus}
                      showSearch={false}
                      value={values.status}
                      onChange={(value) => setFieldValue("status", value)}
                    />
                  </Col>
                </Row>
                <Row>
                  <Col sm={12} className="fund-form__field">
                    <InputField
                      placeholder={"Management Company Name *"}
                      name="companyName"
                      type="text"
                      value={values?.companyName}
                    />
                  </Col>
                  <Col sm={12} className="fund-form__field">
                    <InputField
                      placeholder={"Investment Manager Name *"}
                      name="investmentManagerName"
                      type="text"
                      value={values?.investmentManagerName}
                    />
                  </Col>
                </Row>
                <Row>
                  <Col sm={12} className="fund-form__field">
                    <InputField
                      placeholder={"Sub Advisor (if applicable)"}
                      name="subAdvisorName"
                      type="text"
                      value={values?.subAdvisorName}
                    />
                  </Col>
                </Row>
                <Row>
                  <Col sm={12} className="fund-form__field">
                    <DropdownField
                      mode="single"
                      optionFilterProp="label"
                      placeholder={"Strategy"}
                      name="strategy"
                      setFieldTouched={() => setFieldTouched('strategy')}
                      options={fundStrategies}
                      showSearch={false}
                      value={values.strategy}
                      onChange={(value) => setFieldValue("strategy", value)}
                    />
                  </Col>
                  <Col sm={12} className="fund-form__field">
                    <DropdownField
                      mode="multiple"
                      optionFilterProp="label"
                      placeholder={"Sub Strategy (if applicable)"}
                      name="subStrategy"
                      setFieldTouched={() => setFieldTouched('subStrategy')}
                      options={fundSubStrategies}
                      showSearch={false}
                      value={values.subStrategy}
                      onChange={(value) => setFieldValue("subStrategy", value)}
                    />
                  </Col>
                </Row>
                <Row>
                  <Col sm={12} className="fund-form__field">
                    <DropdownField
                      mode="multiple"
                      optionFilterProp="label"
                      placeholder={"Products Traded"}
                      name="productsTraded"
                      options={productsTraded}
                      showSearch={false}
                      setFieldTouched={() => setFieldTouched('productsTraded')}
                      value={values.productsTraded}
                      onChange={(value) =>
                        setFieldValue("productsTraded", value)
                      }
                    />
                  </Col>
                  <Col sm={12} className="fund-form__field">
                    <DropdownField
                      mode="multiple"
                      optionFilterProp="label"
                      placeholder={"Geographic Focus/Exposure"}
                      name="geographicFocus"
                      options={geographicFocus}
                      showSearch={false}
                      setFieldTouched={() => setFieldTouched('geographicFocus')}
                      value={values.geographicFocus}
                      onChange={(value) =>
                        setFieldValue("geographicFocus", value)
                      }
                    />
                  </Col>
                </Row>
                <Row>
                  <Col sm={12} className="fund-form__field">
                    <DatePickerComponent
                      placeholder={"Target Launch Date"}
                      name="targetLaunchDate"
                      className="input-field"
                      value={
                        values?.targetLaunchDate
                        && moment(values?.targetLaunchDate)
                      }
                      onChange={(value, dateString) => {
                        setFieldValue("targetLaunchDate", dateString);
                      }}
                    />
                  </Col>
                </Row>
                <Row>
                  <Col sm={12} className="fund-form__field">
                    <InputField
                      placeholder={"Target AUM"}
                      name="targetAum"
                      type="number"
                      value={values?.targetAum}
                    />
                  </Col>
                  {values.status === FundStatusBE.ACTIVE && (
                    <>
                      <Col sm={12} className="fund-form__field">
                        <InputField
                          placeholder={"Current AUM"}
                          name="aum"
                          type="number"
                          value={values?.aum}
                        />
                      </Col>
                      <Col sm={12} className="fund-form__field">
                        <InputField
                          placeholder={"AUM at launch"}
                          name="aumAtLaunch"
                          type="number"
                          value={values?.aumAtLaunch}
                        />
                      </Col>
                    </>
                  )}
                  <Col sm={12} className="fund-form__field">
                    <DropdownField
                      mode="single"
                      optionFilterProp="label"
                      placeholder={"Fund Base Currency"}
                      name="baseCurrency"
                      options={fundBaseCurrencies}
                      showSearch={false}
                      setFieldTouched={() => setFieldTouched('baseCurrency')}
                      value={values.baseCurrency}
                      onChange={(value) => setFieldValue("baseCurrency", value)}
                    />
                  </Col>
                </Row>
                <Row>
                  <Col sm={12} className="fund-form__field">
                    <InputField
                      placeholder={"Leverage (Long)"}
                      name="leverageLong"
                      type="text"
                      value={values?.leverageLong}
                    />
                  </Col>
                  <Col sm={12} className="fund-form__field">
                    <InputField
                      placeholder={"Leverage (Short)"}
                      name="leverageShort"
                      type="text"
                      value={values?.leverageShort}
                    />
                  </Col>
                </Row>
                <Row>
                  <Col sm={12} className="fund-form__field">
                    <InputField
                      placeholder={"Leverage (Net)"}
                      name="leverageNet"
                      type="text"
                      value={values.leverageNet}
                    />
                  </Col>
                </Row>
                <Row />
                <Row>
                  <Col sm={12} className="fund-form__field">
                    <InputField placeholder={"LEI"} name="lei" type="text" value={values.lei} />
                  </Col>
                  <Col sm={12} className="fund-form__field">
                    <InputField
                      placeholder={"General Partner if applicable/Platform"}
                      name="partnerName"
                      type="text"
                      value={values.partnerName}
                    />
                  </Col>
                </Row>
                <Row>
                  <Col sm={12} className="fund-form__field">
                    <InputField
                      placeholder={"Fund Administrator"}
                      name="fundAdministrator"
                      type="text"
                      value={values.fundAdministrator}
                    />
                  </Col>
                </Row>
                <Row>
                  <Col sm={12} className="fund-form__field">
                    <InputField
                      placeholder={"Registered Address *"}
                      name="addresses[0].street"
                      type="text"
                      value={values.addresses[0]?.street}
                    />
                  </Col>
                  <Col sm={12} className="fund-form__field">
                    <InputField
                      placeholder={"Address 2"}
                      name="addresses[0].addressLine"
                      type="text"
                      value={values.addresses[0]?.addressLine}
                    />
                  </Col>
                </Row>
                <Row>
                  <Col sm={12} className="fund-form__field">
                    <DropdownField
                      mode="single"
                      optionFilterProp="label"
                      placeholder={"Country *"}
                      name="addresses[0].countryId"
                      options={countries}
                      showSearch={false}
                      value={values.addresses[0]?.countryId}
                      defaultValue={values.addresses[0]?.countryId}
                      setFieldTouched={() => setFieldTouched('addresses[0].countryId')}
                      onChange={(value) => {
                        setFieldValue("addresses[0].countryId", value);
                        fetchProvinces(value);
                      }}
                    />
                  </Col>
                  <Col sm={12} className="fund-form__field">
                    <DropdownField
                      mode="single"
                      optionFilterProp="label"
                      placeholder={"State/Province *"}
                      name="addresses[0].provinceId"
                      options={provinces}
                      showSearch={false}
                      value={values.addresses[0]?.provinceId}
                      defaultValue={values.addresses[0]?.provinceName}
                      setFieldTouched={() => setFieldTouched('addresses[0].provinceId')}
                      onChange={(value) => {
                        setFieldValue("addresses[0].provinceId", value);
                        fetchCities(values?.addresses[0]?.countryId, value);
                      }}
                    />
                  </Col>
                </Row>
                <Row>
                  <Col sm={12} className="fund-form__field">
                    <DropdownField
                      mode="single"
                      optionFilterProp="label"
                      placeholder={"City *"}
                      name="addresses[0].cityId"
                      options={cities}
                      showSearch={false}
                      value={values.addresses[0]?.cityId}
                      setFieldTouched={() => setFieldTouched('addresses[0].cityId')}
                      onChange={(value) => {
                        setFieldValue("addresses[0].cityId", value);
                        setFieldValue("addresses[0].addressType", "registered");
                      }}
                    />
                  </Col>
                  <Col sm={12} className="fund-form__field">
                    <InputField
                      placeholder={"Zip Code/Postal Code *"}
                      name="addresses[0].zipcode"
                      type="text"
                      value={values.addresses[0]?.zipcode}
                    />
                  </Col>
                </Row>
              </div>
            </Form>
          </UIModal>
        );
      }}
    </Formik>
  );
};

export default FundForm;
